import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";

const AuthContext = createContext(null);

// Öffentliche Routen — auf diesen Pfaden führen wir keinen /auth/me-Ping durch,
// weil dort keine Session erwartet wird und ein 401 nur unnötiges Console-Rauschen
// erzeugen würde (Browser loggt "Failed to load resource: 401" auf Netzwerk-Ebene).
const PUBLIC_PATH_PREFIXES = [
  "/",
  "/impressum",
  "/datenschutz",
  "/sicherheit",
  "/login",
  "/register",
  "/passwort-vergessen",
  "/passwort-zuruecksetzen",
  "/waitlist",
  "/notfall-zugang",
  "/karte-sperren",
];

function isKnownPublicPath(pathname) {
  if (!pathname) return false;
  if (pathname === "/") return true;
  return PUBLIC_PATH_PREFIXES.some(
    (p) => p !== "/" && (pathname === p || pathname.startsWith(p + "/"))
  );
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(null);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If returning from OAuth, skip /me check so AuthCallback establishes the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Auf reinen öffentlichen Seiten kein /me anfragen — spart einen erwartbaren 401
    // und dessen Browser-Console-Log, ohne die Backend-Auth abzuschwächen.
    if (typeof window !== "undefined" && isKnownPublicPath(window.location.pathname)) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
